import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { X, Download, Smartphone } from 'lucide-react';

export function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  // Two installable apps share this origin: the guest booking site and the team ops app.
  const isTeamApp = !(
    window.location.pathname === '/' ||
    window.location.pathname.startsWith('/stay') ||
    window.location.pathname.startsWith('/book')
  );
  const appName = isTeamApp ? 'Tarashi Team' : 'Tarashi Stays';

  useEffect(() => {
    // Check if already installed (standalone mode)
    const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                       window.navigator.standalone === true;
    setIsStandalone(standalone);

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(iOS);

    // Listen for beforeinstallprompt event (Android/Chrome)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show prompt after a delay if not dismissed before
      const dismissed = localStorage.getItem('pwa-prompt-dismissed');
      if (!dismissed) {
        setTimeout(() => setShowPrompt(true), 3000);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Show iOS prompt if on iOS and not installed
    if (iOS && !standalone) {
      const dismissed = localStorage.getItem('pwa-prompt-dismissed');
      if (!dismissed) {
        setTimeout(() => setShowPrompt(true), 3000);
      }
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowPrompt(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-prompt-dismissed', 'true');
  };

  // Don't show if already installed
  if (isStandalone || !showPrompt) return null;

  return (
    <div 
      className="fixed bottom-20 left-4 right-4 sm:left-auto sm:right-4 sm:w-80 bg-white rounded-2xl shadow-2xl border border-slate-200 p-4 z-50 animate-in slide-in-from-bottom-5"
      data-testid="pwa-install-prompt"
    >
      <button 
        onClick={handleDismiss}
        className="absolute top-3 right-3 text-slate-400 hover:text-slate-600"
        aria-label="Dismiss"
      >
        <X className="w-5 h-5" />
      </button>
      
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
          <Smartphone className="w-6 h-6 text-emerald-600" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-slate-900 text-sm">Install {appName}</h3>
          <p className="text-xs text-slate-500 mt-1">
            {isIOS 
              ? 'Tap the share button and select "Add to Home Screen"'
              : isTeamApp
                ? 'Install the team app for check-ins, tasks and calendars on your home screen'
                : 'Install for quick access from your home screen'
            }
          </p>
        </div>
      </div>
      
      {!isIOS && (
        <Button 
          onClick={handleInstall}
          className="w-full mt-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
          data-testid="install-app-btn"
        >
          <Download className="w-4 h-4 mr-2" />
          Install App
        </Button>
      )}
      
      {isIOS && (
        <div className="mt-4 p-3 bg-slate-50 rounded-xl">
          <div className="flex items-center gap-2 text-xs text-slate-600">
            <span>1. Tap</span>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
            </svg>
            <span>Share</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-600 mt-2">
            <span>2. Select "Add to Home Screen"</span>
          </div>
        </div>
      )}
    </div>
  );
}

// Service Worker Registration with auto-update handling
export function registerServiceWorker() {
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', async () => {
      try {
        const registration = await navigator.serviceWorker.register('/sw.js');
        console.log('SW registered:', registration.scope);

        // Check for a new worker straight away, not just on the 60s timer. Without this
        // a visitor carrying an old worker can keep booting a stale app bundle for up
        // to a minute — long enough to land on the wrong route.
        registration.update();

        // Check for updates periodically
        setInterval(() => {
          registration.update();
        }, 60000); // Check every minute
        
        // Handle updates
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          console.log('SW update found');
          
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // New version available
              console.log('New SW version available');
              // Skip waiting and activate immediately
              newWorker.postMessage({ type: 'SKIP_WAITING' });
            }
          });
        });
        
        // Listen for controller change (new SW took over)
        navigator.serviceWorker.addEventListener('controllerchange', () => {
          console.log('SW controller changed, reloading for updates');
          // Soft reload to get new version
          window.location.reload();
        });
        
      } catch (error) {
        console.log('SW registration failed:', error);
      }
    });
  }
}
